import type { KitItem, OrcamentoResultado } from './calc';
import { brl } from './calc';
import { imprimirHtml } from '@/lib/arquivos';

export interface DadosComprovante {
  cliente: { nome: string; cidade: string; uf: string; animais: string };
  itens: KitItem[];
  resultado: OrcamentoResultado;
  softwarePlano: string;
  softwareMensal: number;
  softwareMeses: number;
  impostoVendaPct: number; // alíquota sobre a venda (regime) — configurável
  custoBrincoReais: number; // custo real do brinco (fornecedor) por unidade
}

const esc = (s: string) =>
  String(s || '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] || c
  );
const pad2 = (n: number) => String(n).padStart(2, '0');
const pct = (v: number) => `${(v || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

// Comprovante de venda INTERNO — mostra custo, impostos e lucro. Uso interno,
// nunca enviar ao cliente (leva um aviso vermelho no topo).
export function gerarComprovanteInterno(d: DadosComprovante) {
  const origin = window.location.origin;
  const agora = new Date();
  const dataStr = `${pad2(agora.getDate())}/${pad2(agora.getMonth() + 1)}/${agora.getFullYear()}`;
  const r = d.resultado;

  // Receita
  const recHardware = r.hardwareVenda;
  const recBrincos = r.brincoLiquido;
  const recFrete = r.freteNacional;
  const recSoftware = d.softwareMensal * d.softwareMeses;
  const receitaTotal = recHardware + recBrincos + recFrete + recSoftware;

  // Custos
  const custoBrincos = (d.custoBrincoReais || 0) * r.brincoQtd;
  const custoFrete = recFrete; // repasse: custo = receita
  const custoTotal = r.custoHardware + custoBrincos + custoFrete;

  // Lucro
  const lucroHardware = r.valorMargem; // hardwareVenda - custoHardware
  const lucroBrincos = recBrincos - custoBrincos;
  const lucroBruto = lucroHardware + lucroBrincos + recSoftware;

  // Imposto sobre a venda (exclui frete, que é repasse)
  const baseImpostoVenda = recHardware + recBrincos + recSoftware;
  const impostoVenda = baseImpostoVenda * ((d.impostoVendaPct || 0) / 100);
  const lucroLiquido = lucroBruto - impostoVenda;
  const margemLiquidaPct = receitaTotal > 0 ? (lucroLiquido / receitaTotal) * 100 : 0;

  const alerta = (cond: boolean, txt: string) =>
    cond ? ` <span class="warn">(${txt})</span>` : '';

  const html = `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8">
<title>Comprovante interno Curral Tech</title>
<style>
  :root{ --brand:#0b3d5c; --ink:#1d2b39; --muted:#6b7a8a; --line:#dfe6ee; --green:#2f7d32; --red:#c0392b; }
  *{ box-sizing:border-box; }
  @page{ size:A4; margin:14mm; }
  body{ font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Arial,sans-serif; color:var(--ink); font-size:12px; line-height:1.4; padding:26px 34px; -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  .flag{ background:#fff; border:1px solid var(--red); border-left:4px solid var(--red); color:var(--red); font-weight:700; font-size:10.5px; text-transform:uppercase; letter-spacing:.5px; padding:7px 12px; border-radius:4px; margin-bottom:14px; }
  .top{ display:flex; justify-content:space-between; align-items:flex-start; }
  .brand{ display:flex; align-items:center; gap:12px; }
  .brand img{ height:38px; filter:grayscale(1) contrast(1.05); }
  .brand .name{ font-family:Georgia,"Times New Roman",serif; font-size:16px; font-weight:700; letter-spacing:1.5px; color:#222; }
  .brand .tag{ font-size:10px; color:var(--muted); }
  .meta{ text-align:right; font-size:11px; color:var(--muted); }
  h1{ font-family:Georgia,"Times New Roman",serif; font-size:17px; color:var(--brand); margin:12px 0 2px; }
  .sub{ color:var(--muted); font-size:12px; margin-bottom:10px; }
  h2{ font-size:10.5px; text-transform:uppercase; letter-spacing:.8px; color:var(--brand); margin:14px 0 4px; font-weight:700; }
  table{ width:100%; border-collapse:collapse; }
  td{ padding:5px 6px; border-bottom:1px solid var(--line); }
  .num{ text-align:right; white-space:nowrap; }
  .muted{ color:var(--muted); }
  .warn{ color:var(--red); font-size:10.5px; }
  .sub-t td{ color:var(--muted); }
  .tot td{ font-weight:700; border-top:2px solid var(--brand); }
  .lucro{ margin-top:14px; border:1.5px solid var(--green); border-radius:6px; padding:12px 16px; display:flex; justify-content:space-between; align-items:center; background:#fff; }
  .lucro .lbl{ font-size:11px; text-transform:uppercase; letter-spacing:.5px; color:var(--muted); }
  .lucro .big{ font-size:22px; font-weight:800; color:var(--green); }
  .lucro .mini{ font-size:11px; color:var(--muted); }
  @media print{ body{ padding:0; } }
</style></head>
<body onload="window.print()">
  <div class="flag">Uso interno. Não enviar ao cliente.</div>

  <div class="top">
    <div class="brand">
      <img src="${origin}/logo.png" alt="Curral Tech">
      <div><div class="name">CURRAL TECH</div><div class="tag">Análise de venda</div></div>
    </div>
    <div class="meta">Comprovante interno<br>Data: <b>${dataStr}</b></div>
  </div>

  <h1>Comprovante de venda (interno)</h1>
  <div class="sub">Cliente: ${esc(d.cliente.nome) || 'não informado'}${d.cliente.cidade ? `, ${esc(d.cliente.cidade)}/${esc(d.cliente.uf)}` : ''}${d.cliente.animais ? `, ${esc(d.cliente.animais)} animais` : ''}</div>

  <h2>Receita (o que o cliente paga)</h2>
  <table>
    <tr><td>Equipamentos (leitores + aplicador)</td><td class="num">${brl(recHardware)}</td></tr>
    ${r.brincoQtd > 0 ? `<tr><td>Brincos (${r.brincoQtd} un)</td><td class="num">${brl(recBrincos)}</td></tr>` : ''}
    <tr><td>Frete (repasse)</td><td class="num">${brl(recFrete)}</td></tr>
    ${recSoftware > 0 ? `<tr><td>Software ${esc(d.softwarePlano)} (${d.softwareMeses} meses)</td><td class="num">${brl(recSoftware)}</td></tr>` : ''}
    <tr class="tot"><td>Receita total</td><td class="num">${brl(receitaTotal)}</td></tr>
  </table>

  <h2>Custos</h2>
  <table>
    <tr><td>Custo do hardware (landed, importação)</td><td class="num">${brl(r.custoHardware)}</td></tr>
    <tr class="sub-t"><td>&nbsp;&nbsp;• FOB (US$ → R$)</td><td class="num">${brl(r.fobImportadosBrl)}</td></tr>
    <tr class="sub-t"><td>&nbsp;&nbsp;• Imposto de importação (II)</td><td class="num">${brl(r.valorII)}</td></tr>
    <tr class="sub-t"><td>&nbsp;&nbsp;• ICMS importação (por dentro)</td><td class="num">${brl(r.valorICMS)}</td></tr>
    ${r.brincoQtd > 0 ? `<tr><td>Custo dos brincos${alerta(!d.custoBrincoReais, 'informe o custo do fornecedor')}</td><td class="num">${brl(custoBrincos)}</td></tr>` : ''}
    <tr><td>Frete (repasse)</td><td class="num">${brl(custoFrete)}</td></tr>
    <tr class="tot"><td>Custo total</td><td class="num">${brl(custoTotal)}</td></tr>
  </table>

  <h2>Lucro e impostos</h2>
  <table>
    <tr><td>Lucro no hardware (margem)</td><td class="num">${brl(lucroHardware)}</td></tr>
    ${r.brincoQtd > 0 ? `<tr><td>Lucro nos brincos</td><td class="num">${brl(lucroBrincos)}</td></tr>` : ''}
    ${recSoftware > 0 ? `<tr><td>Software (assinatura)</td><td class="num">${brl(recSoftware)}</td></tr>` : ''}
    <tr class="tot"><td>Lucro bruto</td><td class="num">${brl(lucroBruto)}</td></tr>
    <tr><td>Imposto sobre a venda (${pct(d.impostoVendaPct)})${alerta(!d.impostoVendaPct, 'defina a alíquota do regime')}</td><td class="num">− ${brl(impostoVenda)}</td></tr>
  </table>

  <div class="lucro">
    <div>
      <div class="lbl">Lucro líquido estimado</div>
      <div class="mini">Margem líquida sobre a receita: ${pct(margemLiquidaPct)}</div>
    </div>
    <div class="big">${brl(lucroLiquido)}</div>
  </div>
</body></html>`;

  void imprimirHtml(html, 'Comprovante de venda');
}
